// ============================================================================
// Reading hub (spec §10.1.3 kin) — the REVERSE orientation: start from a Korean
// sound-reading (음/eum) you heard and see every Hanja that shares it, using
// meaning to disambiguate. With no eum, shows the browsable index of readings
// shared by more than one character.
// ============================================================================

import { el, mount } from '../dom.js';
import * as api from '@core/data/api.js';
import { romanize } from '@core/hangul/romanize.js';
import { speakerButton } from '../audio.js';
import { romanizationOn } from '../prefs.js';

export function renderReading({ navigate, eum }) {
  const container = el('div.container.stack');
  mount(container, el('div.card', { text: 'Loading…' }));

  const back = el('button.btn.btn--ghost', { text: '← Browse', onclick: () => navigate('#/browse') });

  if (eum) renderHub(eum);
  else renderIndex();

  async function renderHub(reading) {
    try {
      const hub = await api.readingHub(reading);
      const chars = hub?.characters || [];

      const header = el('div.card.stack.center', {}, [
        el('div.flashcard__word', { text: reading }),
        el('div.row.center', { style: 'justify-content:center' }, [speakerButton(() => reading, { label: '🔊 Listen' })]),
        romanizationOn() ? el('div.faint', { text: romanize(reading) }) : false,
        el('div.muted', {
          text: chars.length === 1
            ? 'Only one character in the set uses this reading.'
            : `${chars.length} characters share this sound — meaning and context tell them apart.`,
        }),
      ]);

      const rows = chars.map((c) =>
        el('button.card.browse-card', { onclick: () => navigate(`#/item/character/${c.id}`) }, [
          el('div.browse-card__glyph', { text: c.char }),
          el('div.stack', { style: 'gap:2px' }, [
            el('div.browse-card__reading', { text: `${c.hun ?? ''} ${reading}`.trim() }),
            el('div.muted', { text: c.meaning_en || '—' }),
            el('div.faint', {
              text: [
                c.radical_char || c.radical ? `radical ${c.radical_char || c.radical}` : null,
                c.stroke_count ? `${c.stroke_count} strokes` : null,
                c.word_count ? `${c.word_count} word${c.word_count === 1 ? '' : 's'}` : 'no words yet',
              ].filter(Boolean).join(' · '),
            }),
          ]),
        ]));

      mount(container,
        el('div.row', {}, [back,
          el('button.btn.btn--ghost', { text: 'All readings →', onclick: () => navigate('#/reading') })]),
        header,
        chars.length ? el('div.browse-grid', {}, rows) : el('div.card', {}, [el('p.muted', { text: 'No characters found for this reading.' })]),
      );
    } catch (e) {
      mount(container, el('div.card.stack', {}, [back, el('div.banner.banner--warn', { text: e.message || 'Could not load reading' })]));
    }
  }

  async function renderIndex() {
    try {
      const list = await api.readingIndex();

      const search = el('input.input', {
        type: 'search',
        placeholder: 'Jump to a reading (가, 사, 학…) or its romanization…',
      });
      const grid = el('div.browse-grid');

      function paint(filter) {
        const f = (filter || '').trim().toLowerCase();
        const rows = (f
          ? list.filter((r) => r.eum.includes(f) || romanize(r.eum).toLowerCase().includes(f))
          : list);
        grid.replaceChildren(...(rows.length
          ? rows.map((r) => el('button.card.browse-card', { onclick: () => navigate(`#/reading/${encodeURIComponent(r.eum)}`) }, [
              el('div.browse-card__glyph.browse-card__glyph--ko', { text: r.eum }),
              el('div.stack', { style: 'gap:2px' }, [
                romanizationOn() ? el('div.faint', { text: romanize(r.eum) }) : false,
                el('div.muted', { text: `${r.n_hanja} Hanja` }),
              ]),
            ]))
          : [el('p.muted', { text: 'No readings match.' })]));
      }
      search.oninput = () => paint(search.value);

      mount(container,
        el('div.row', {}, [back]),
        el('div.card.stack', {}, [
          el('h1', { text: 'Readings' }),
          el('p.muted', { text: `${list.length} sound-readings are shared by more than one Hanja. Pick one to see every character it could mean — meaning and context tell them apart.` }),
          search,
        ]),
        grid,
      );
      paint('');
    } catch (e) {
      mount(container, el('div.card.stack', {}, [back, el('div.banner.banner--warn', { text: e.message || 'Could not load readings' })]));
    }
  }

  return container;
}
